import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useChartColors } from './theme';
import { useTooltip } from './ChartTooltip';
import TableView from './TableView';
import { useChartWidth } from './useChartWidth';

interface BeeswarmProps { minutes: number[]; height?: number; }

const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

export default function Beeswarm({ minutes, height = 140 }: BeeswarmProps) {
    const { t } = useTranslation('dashboard');
    const c = useChartColors();
    const { show, hide, node } = useTooltip();
    const [showTable, setShowTable] = useState(false);
    const [wrapRef, W] = useChartWidth();

    if (minutes.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{t('charts.noPunchesToday')}</Typography>;
    }

    const H = height, padX = 20, padB = 22, r = 5;
    const min = 6 * 60, max = 20 * 60; // 06:00–20:00 window
    const plotW = W - padX * 2, midY = (H - padB) / 2;
    const plotTop = 0, plotBottom = H - padB;
    const xOf = (m: number) => padX + plotW * ((Math.min(max, Math.max(min, m)) - min) / (max - min));

    // Collision stacking: sort by x, then group consecutive points whose x-gap is
    // below the collision threshold into clusters. Within each cluster, assign each
    // member a distinct vertical offset by its index (0, -1, +1, -2, +2, ...) so no
    // two dots ever land on the same (x, y). This is a straight single pass over
    // sorted input — every value is visited exactly once, so it always yields one
    // placed point per input value, and it terminates trivially (no inner while-loop
    // search for a free slot).
    const sorted = [...minutes].sort((a, b) => a - b);
    const clusterThreshold = r * 1.6;
    const clusters: number[][] = [];
    for (const m of sorted) {
        const x = xOf(m);
        const current = clusters[clusters.length - 1];
        const lastX = current ? xOf(current[current.length - 1]) : null;
        if (current && lastX !== null && Math.abs(lastX - x) < clusterThreshold) {
            current.push(m);
        } else {
            clusters.push([m]);
        }
    }
    const placed: { x: number; y: number; m: number }[] = [];
    for (const cluster of clusters) {
        cluster.forEach((m, idx) => {
            const offset = Math.ceil(idx / 2) * (r * 1.7) * (idx % 2 ? -1 : 1);
            const rawY = midY + offset;
            // Clamp so the marker circle never crosses the plot band edges, even
            // when a same-minute cluster (e.g. a batch biometric clock-in) is
            // large enough for the zigzag offset to overshoot the viewBox.
            const y = Math.max(plotTop + r, Math.min(plotBottom - r, rawY));
            placed.push({ x: xOf(m), y, m });
        });
    }

    return (
        <Box ref={wrapRef}>
            <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', maxWidth: '100%' }} role="img" aria-label={t('charts.arrivalTimesAria')}>
                {[6, 9, 12, 15, 18].map(h => (
                    <text key={h} x={xOf(h * 60)} y={H - 6} textAnchor="middle" fontSize={10} fill={c.muted}>{`${h}:00`}</text>
                ))}
                {placed.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={r} fill={c.series[0]} stroke={c.surface} strokeWidth={2}
                            onMouseMove={e => show(e.clientX, e.clientY, hhmm(p.m))} onMouseLeave={hide} />
                ))}
            </svg>
            <Button size="small" onClick={() => setShowTable(s => !s)}>{t('charts.tableView')}</Button>
            {showTable && <TableView columns={[t('charts.arrival')]} rows={sorted.map(m => [hhmm(m)])} />}
            {node}
        </Box>
    );
}
